import React, { useMemo, useCallback, useId } from "react";
import { useSelector } from "react-redux";
import { Autocomplete, Box, InputAdornment, TextField } from "@mui/material";

import TextFiledWithLabel from "./TextFieldWithLabel";
import { COUNTRY_CODE_LIST } from "../Utility/countryCodes";
import { handleValidateCodevalue } from "../Utility/constant";
import { isValidPhoneNumber } from "../Utility/apiHelper";

const PhoneNumberField = ({
  maxLength = 15,
  name,
  id,
  countryCodeName = "countryCode",
  countryCode = "+91",
  isDetailPage = false,
  isViewMode = false,
  label,
  value = "",
  handleOnChange,
  isRequired = false,
  countryCodeWidth,
  ...otherProps
}) => {
  const generatedId = useId();
  const fieldId =
    id || `${name || "phone-number-field"}-${generatedId.replace(/:/g, "")}`;
  const countryCodeFieldWidth = countryCodeWidth || 80;
  const internationalise = useSelector(
    (state) => state?.internationalise?.currentHashMapData
  );

  const countryCodeOptions = useMemo(() => {
    return COUNTRY_CODE_LIST.map((item) => ({
      ...item,
      value: item.countryCode,
      label: `${item.countryCode} ${item.ISOCode}`,
    }));
  }, []);

  const selectedCountryCode = useMemo(() => {
    return (
      countryCodeOptions.find((item) => item.value === countryCode) ||
      countryCodeOptions.find((item) => item.value === "+91") ||
      null
    );
  }, [countryCodeOptions, countryCode]);


  const isInvalid = useMemo(() => {
    if (!value) return isRequired;

    return !isValidPhoneNumber(value, countryCode);
  }, [value, isRequired, countryCode, isValidPhoneNumber]);

  const handlePhoneChange = useCallback(
    (e) => {
      const sanitized = String(e.target.value || "")
        .replace(/\D/g, "")
        .slice(0, maxLength);

      handleOnChange?.({
        target: {
          name,
          value: sanitized,
        },
      });
    },
    [handleOnChange, maxLength, name]
  );

  const handleCountryCodeChange = useCallback(
    (_, selectedOption) => {
      handleOnChange?.({
        target: {
          name: countryCodeName,
          value: selectedOption?.countryCode || "+91",
        },
      });
    },
    [handleOnChange, countryCodeName]
  );

  return (
    <TextFiledWithLabel
      id={fieldId}
      name={name}
      isViewMode={isViewMode}
      label={
        label || handleValidateCodevalue(internationalise, "COMMON_PHONE_NUMBER")
      }
      value={value}
      handleOnChange={handlePhoneChange}
      inputProps={{
        id: fieldId,
        inputMode: "numeric",
        pattern: "[0-9]*",
        maxLength: maxLength,
      }}
      InputProps={{
        sx: {
          "& .MuiInputAdornment-positionStart": {
            flexShrink: 0,
          },
        },
        startAdornment: (
          <InputAdornment
            position="start"
            sx={{
              mr: 1,
              width: countryCodeFieldWidth,
              flexShrink: 0,
              overflow: "hidden",
              height: "100%",
              maxHeight: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: "flex",
                alignItems: "center",
                width: countryCodeFieldWidth,
                flexShrink: 0,
                overflow: "hidden",
                backgroundColor: "#e3eefa",
              }}
            >
              <Autocomplete
                id={`${fieldId}-country-code`}
                size="small"
                disabled={isViewMode}
                disableClearable
                options={countryCodeOptions}
                value={selectedCountryCode}
                onChange={handleCountryCodeChange}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, selectedValue) =>
                  option?.countryCode === selectedValue?.countryCode
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box component="span" sx={{ minWidth: 48 }}>
                      {option.countryCode}
                    </Box>
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {option.ISOCode}
                    </Box>
                  </Box>
                )}
                slotProps={{
                  paper: {
                    sx: {
                      width: 220,
                      mt: 1,
                    },
                  },
                  popper: {
                    sx: {
                      width: "220px !important",
                    },
                  },
                }}
                sx={{
                  width: countryCodeFieldWidth,
                  flexShrink: 0,
                  boxSizing: "border-box",
                  backgroundColor: "#e3eefa",
                  "& .MuiInputBase-root": {
                    padding: "0 17px 0 6px !important",
                    minHeight: "24px",
                    height: "24px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                  },

                  "& .MuiInputBase-input": {
                    padding: "0 !important",
                    fontSize: "13px",
                    cursor: "pointer",
                    minWidth: "0 !important",
                    width: "100% !important",
                  },

                  "& .MuiAutocomplete-input": {
                    padding: "0 !important",
                  },

                  "& .MuiAutocomplete-endAdornment": {
                    right: "0 !important",
                    top: "50%",
                    transform: "translateY(-50%)",
                  },

                  "& .MuiAutocomplete-popupIndicator": {
                    padding: "1px",
                  },

                  "& .MuiSvgIcon-root": {
                    fontSize: "16px",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="+91"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                    style={{
                      width: countryCodeFieldWidth,
                      margin: 0,
                      marginTop: 0,
                    }}
                    sx={{
                      width: countryCodeFieldWidth,
                      boxSizing: "border-box",
                      "&.MuiFormControl-fullWidth": {
                        width: countryCodeFieldWidth,
                      },
                      "& .MuiInputBase-root": {
                        width: countryCodeFieldWidth,
                        boxSizing: "border-box",
                      },

                      "& .MuiInputBase-input": {
                        padding: "0 !important",
                        fontSize: "13px",
                        width: "100%",
                      },
                    }}
                  />
                )}
              />
            </Box>
          </InputAdornment>
        ),
      }}
      error={isDetailPage && isInvalid}
      helperText={
        isDetailPage && isInvalid
          ? handleValidateCodevalue(internationalise, "COMMON_MSG_VALID_PHONE")
          : " "
      }
      {...otherProps}
    />
  );
};

export default PhoneNumberField;
